import React from 'react';
import Header from '../Header';
import Footer from '../Footer';
import { Link } from 'react-router-dom';

function RegChatPage() {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-16 pb-8 px-4 md:px-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
                RegChat™
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold text-[#191C85] mb-4">
                AI-powered Global Regulatory Intelligence Agent
              </h2>
              <p className="text-xl md:text-2xl text-gray-700 font-medium">
                Regulatory Intelligence at your fingertips
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 text-center mb-6">
                Navigate global regulatory requirements with confidence. RegChat connects you to official guidance, ethics standards, and compliance documents from 135 countries—delivering instant, AI-powered answers, summarizations, and comparisons with direct links to the source.
              </p>
              <div className="text-center">
                <a href="https://youtu.be/Oq8eKJbQQFs" target="_blank" rel="noopener noreferrer">
                  <button className="bg-[#191C85] hover:bg-[#191C85]/90 text-white font-semibold py-3 px-6 rounded-xl transition duration-300 inline-flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                    See How It Works
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Grid */}
        <section className="py-1 px-4 md:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-2xl font-bold text-[#191C85] mb-8 text-center">RegChat delivers:</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
                <div className="text-3xl mb-3">🌍</div>
                <h4 className="font-bold text-gray-900 mb-2">Multi-agency Queries</h4>
                <p className="text-gray-600 text-sm">Get answers from international agencies in seconds.</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
                <div className="text-3xl mb-3">🔗</div>
                <h4 className="font-bold text-gray-900 mb-2">Source Links</h4>
                <p className="text-gray-600 text-sm">Dive deeper with direct links to source documents.</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
                <div className="text-3xl mb-3">⚖️</div>
                <h4 className="font-bold text-gray-900 mb-2">Comparisons</h4>
                <p className="text-gray-600 text-sm">Summarized comparisons of selected regulatory sources.</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
                <div className="text-3xl mb-3">🔄</div>
                <h4 className="font-bold text-gray-900 mb-2">Up-to-Date Intelligence</h4>
                <p className="text-gray-600 text-sm">Stay informed with the latest guidance updates.</p>
              </div>
            </div>
          </div>
        </section>
        {/* The Challenge Section */}
        <section className="py-16 px-4 md:px-8 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-8 text-center">
              The Challenge
            </h2>
            <p className="text-lg text-gray-600 mb-8 text-center max-w-4xl mx-auto">
              Clinical research and manufacturing teams face mounting complexity in staying compliant:
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-[#191C85] mb-3">Ever-changing regulations</h3>
                <p className="text-gray-600">
                  Country-specific rules and evolving guidance create confusion and compliance risks.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-[#191C85] mb-3">Fragmented sources</h3>
                <p className="text-gray-600">
                  Regulatory, ethics, and manufacturing standards are scattered across agencies worldwide.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-[#191C85] mb-3">Inefficient research</h3>
                <p className="text-gray-600">
                  Staff spend hours digging through websites, PDFs, and databases instead of focusing on higher-value work.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The RegChat Solution Section */}
        <section className="py-16 px-4 md:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-12 text-center">
              The RegChat Solution
            </h2>
            
            <div className="space-y-12">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3">
                  <div className="bg-[#191C85] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-[#191C85] mb-3">International Regulatory Questions</h3>
                </div>
                <div className="md:w-2/3 space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Problem:</strong> Teams struggle to find answers and compare evolving global guidance.</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Solution:</strong> RegChat provides instant, conversational answers with direct citations from official documents—across ICH, FDA, EMA, MHRA, PMDA, and 135+ country agencies.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3">
                  <div className="bg-[#191C85] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-[#191C85] mb-3">Global Planning & Compliance</h3>
                </div>
                <div className="md:w-2/3 space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Problem:</strong> CROs, Pharma, Biotech, and CDMOs risk delays and noncompliance when navigating inconsistent requirements for clinical trials, submissions, or manufacturing.</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Solution:</strong> RegChat delivers summarized, side-by-side comparisons of country and agency requirements with source links for deeper examination.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3">
                  <div className="bg-[#191C85] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-[#191C85] mb-3">Data Management Guidance</h3>
                </div>
                <div className="md:w-2/3 space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Problem:</strong> Clinical trial data collection, handling, and quality assurance standards are complex and spread across multiple sources, creating inefficiency and compliance risk.</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Solution:</strong> RegChat's Data Management collection provides instant access to authoritative guidance and best practices for data management and regulatory compliance—reducing errors and saving valuable time.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3">
                  <div className="bg-[#191C85] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-[#191C85] mb-3">ClinicalTrials.gov Assistant</h3>
                </div>
                <div className="md:w-2/3 space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Problem:</strong> Trial registration requirements, results reporting rules, and submission processes can be difficult to interpret and navigate.</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Solution:</strong> RegChat's ClinicalTrials.gov Assistant allows users to efficiently search, interpret, and chat against 500,000+ trial records (protocols, endpoints, biomarkers, inclusion/exclusion criteria) for streamlined planning and submissions.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-8 text-center">
              Why It Matters
            </h2>
            <p className="text-lg text-gray-600 mb-12 text-center max-w-4xl mx-auto">
              RegChat empowers CROs, Pharma, Biotechs, and CDMOs to:
            </p>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="text-3xl mb-3">⏱️</div>
                <h3 className="font-bold text-gray-900 mb-2">Save time</h3>
                <p className="text-gray-600">By replacing hours of manual research across ICH, FDA, EMA, MHRA, and more with instant, source-linked answers.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="text-3xl mb-3">🛡️</div>
                <h3 className="font-bold text-gray-900 mb-2">Reduce risk</h3>
                <p className="text-gray-600">By ensuring compliance with authoritative, always up-to-date regulatory standards and manufacturing compliance guidelines worldwide.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="text-3xl mb-3">🚀</div>
                <h3 className="font-bold text-gray-900 mb-2">Accelerate planning</h3>
                <p className="text-gray-600">With clarity on international requirements across 135+ countries for clinical trials and manufacturing.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="text-3xl mb-3">📊</div>
                <h3 className="font-bold text-gray-900 mb-2">Improve data integrity</h3>
                <p className="text-gray-600">With guidance on best practices for data collection, handling, and quality assurance.</p>
              </div>
            </div>
            <div className="mt-8 max-w-4xl mx-auto bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="text-3xl mb-3 text-center">💬</div>
              <p className="text-gray-700 text-center">
                Chat against <strong>500,000+ ClinicalTrials.gov records</strong> with natural language to quickly search by drug, condition/therapeutic area, endpoints, biomarkers, and inclusion/exclusion criteria.
              </p>
            </div>
          </div>
        </section>

        {/* RegChat Features Section */}
        <section className="py-16 px-4 md:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-12 text-center">
              RegChat Features
            </h2>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 max-w-5xl mx-auto">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Individual and Enterprise accounts</h3>
                  <p className="text-gray-600">Available for all organization sizes</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Single Sign-On (SSO)</h3>
                  <p className="text-gray-600">With role-based and defined user groups for access</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Admin Dashboard</h3>
                  <p className="text-gray-600">For permissions settings and access management</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Monthly Usage Reports</h3>
                  <p className="text-gray-600">Customized reports with user, message and collection analytics</p>
                </div>
              </div>
              <div className="flex items-start md:col-span-2">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Private Hosting</h3>
                  <p className="text-gray-600">Enterprise accounts hosted on Microsoft Azure giving you the same security and compliance as your Microsoft environment</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-8 bg-[#191C85]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              See RegChat in action
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              See how RegChat works <a href="https://youtu.be/Oq8eKJbQQFs" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">here</a>. Test-drive RegChat free for 14 days at <a href="https://RegChat.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">RegChat.com</a> and experience how AI can streamline global regulatory compliance and trial planning for your team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://RegChat.com" target="_blank" rel="noopener noreferrer">
                <button className="bg-white hover:bg-gray-100 text-[#191C85] font-bold py-4 px-8 rounded-xl text-lg transition duration-300">
                  Start Free Trial
                </button>
              </a>
              <Link to="/#contact">
                <button className="bg-transparent border-2 border-white hover:bg-white text-white hover:text-[#191C85] font-bold py-4 px-8 rounded-xl text-lg transition duration-300">
                  Request a Demo
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default RegChatPage;